/**
 * Модалка: ищет компании в Bitrix24 по ИНН организации и показывает
 * список сделок. Клик «Привязать» → пишет deal_url в organization.
 *
 * Использует тот же подход, что battlecard:
 *   GET /api/v1/integrations/bitrix/lookup?inn=...
 */
import { useEffect, useState } from 'react';
import { Loader2, X, Search, Link as LinkIcon, AlertTriangle } from 'lucide-react';
import { api } from '../api/client';
import { orgsApi } from '../api/organizations';

interface BitrixDeal {
  id: string;
  title: string;
  stage_id?: string | null;
  semantic?: string | null;
  category_id?: number | null;
  date_create?: string | null;
  deal_url: string;
}

interface BitrixCompany {
  id: string;
  title: string;
  company_type?: string | null;
  deals: BitrixDeal[];
}

interface LookupOut {
  configured: boolean;
  inn: string;
  companies: BitrixCompany[];
}

const SEMANTIC_LABEL: Record<string, string> = {
  P: 'активная',
  S: 'выиграна',
  F: 'проиграна',
};
const SEMANTIC_COLOR: Record<string, string> = {
  P: 'text-emerald-300',
  S: 'text-blue-300',
  F: 'text-red-300',
};

export default function BitrixDealPicker({ orgId, inn, currentUrl, onClose, onSaved }:
  { orgId: string; inn: string; currentUrl: string | null;
    onClose: () => void; onSaved: () => void }) {
  const [data, setData] = useState<LookupOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const lookup = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get<LookupOut>(`/api/v1/integrations/bitrix/lookup`, { params: { inn } });
      setData(r.data);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'ошибка');
    } finally { setLoading(false); }
  };
  useEffect(() => { lookup(); }, [inn]);

  const bind = async (deal: BitrixDeal) => {
    setBusyId(deal.id);
    try {
      await orgsApi.update(orgId, { bitrix_deal_url: deal.deal_url });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'ошибка');
    } finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl mx-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Search size={16} className="text-blue-400" /> Bitrix24 · поиск по ИНН {inn}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </header>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {!data?.configured && data && (
            <div className="bg-amber-900/30 border border-amber-800 text-amber-200 px-3 py-2 rounded text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>Webhook Bitrix24 не настроен. Задайте <code>bitrix24_webhook</code> в Настройки → Интеграции.</div>
            </div>
          )}
          {err && (
            <div className="bg-red-900/30 border border-red-800 text-red-200 px-3 py-2 rounded text-sm">{err}</div>
          )}
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="animate-spin text-blue-400 mx-auto" /></div>
          ) : data && data.companies.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6">
              Компаний с ИНН <code>{inn}</code> в Bitrix24 не найдено.
            </div>
          ) : (data?.companies || []).map(c => (
            <div key={c.id} className="bg-slate-950 border border-slate-800 rounded-lg p-3">
              <header className="text-sm text-white font-bold">{c.title}</header>
              {c.company_type && (
                <div className="text-[11px] text-slate-500">тип: {c.company_type}</div>
              )}
              {c.deals.length === 0 ? (
                <div className="text-xs text-slate-500 mt-2">Сделок в этой компании нет.</div>
              ) : (
                <div className="mt-2 space-y-1.5">
                  {c.deals.map(d => {
                    const isCurrent = currentUrl === d.deal_url;
                    return (
                      <div key={d.id} className="flex items-center justify-between gap-2 bg-slate-900 px-2.5 py-1.5 rounded text-xs">
                        <div className="flex-1 min-w-0">
                          <a href={d.deal_url} target="_blank" rel="noopener noreferrer"
                            className="text-blue-300 hover:underline truncate inline-block max-w-full">
                            #{d.id} · {d.title}
                          </a>
                          <span className={`ml-2 text-[10px] ${d.semantic ? SEMANTIC_COLOR[d.semantic] || 'text-slate-400' : 'text-slate-400'}`}>
                            {d.semantic ? SEMANTIC_LABEL[d.semantic] || d.semantic : d.stage_id}
                          </span>
                        </div>
                        <button onClick={() => bind(d)} disabled={busyId === d.id || isCurrent}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold transition disabled:opacity-50"
                          style={{
                            background: isCurrent ? '#16a34a' : '#2563eb',
                            color: 'white',
                          }}>
                          {busyId === d.id ? <Loader2 size={11} className="animate-spin" />
                            : <LinkIcon size={11} />}
                          {isCurrent ? 'привязана' : 'Привязать'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="border-t border-slate-800 p-3 flex justify-between items-center text-[11px] text-slate-500">
          <button onClick={lookup} disabled={loading}
            className="inline-flex items-center gap-1 text-blue-400 hover:underline">
            <Search size={11} /> повторить поиск
          </button>
          <span>Поиск через crm.requisite.list + crm.deal.list</span>
        </div>
      </div>
    </div>
  );
}
